import { useState, FC, useContext, useEffect, FocusEvent, ChangeEvent } from "react";
import { FieldProps, getIn, useFormikContext } from "formik";
import { FormFieldContext } from "./formRow/FormRow";

import OutsideClickSub from "../outsideClickSub/OutsideClickSub";
import { useDebounce } from "../../app/hooks";

interface CustomProps {
	label: string;
	disabled?: boolean;
	autosubmit?: boolean;
}

// Native <input type="date"> works with yyyy-MM-dd strings; format a Date to it.
const toInputValue = (date: Date | null): string =>
	date && !Number.isNaN(date.getTime())
		? `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`
		: "";

const CustomCalendarInput: FC<FieldProps & CustomProps> = ({
	field,
	form: { touched, errors },
	disabled,
	label,
	autosubmit,
}) => {
	const error = getIn(errors, field.name);
	const wasTouched = getIn(touched, field.name);
	const [focused, setFocused] = useState<boolean>(false);
	const [startDate, setStartDate] = useState<Date | null>(
		field.value ? new Date(field.value) : null,
	);
	const debouncedDate = useDebounce<Date | null>(startDate, 500);

	const { state, setState } = useContext(FormFieldContext);
	const { setFieldValue, submitForm } = useFormikContext();
	useEffect(() => {
		const newState = focused
			? "focused"
			: !!(wasTouched && error)
				? "error"
				: field.value
					? "idleValue"
					: "idle";
		if (state !== newState) {
			setState(newState);
		}
	}, [error, wasTouched, focused, field.value, setState, state]);

	useEffect(() => {
		if (autosubmit && wasTouched && submitForm) submitForm();
	}, [debouncedDate]);

	const handleFocus = () => setFocused(true);

	const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
		field.onBlur(e);
		setFocused(false);
	};
	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value ? new Date(e.target.value) : null;
		// Preserve the Date-object value contract of the previous implementation.
		setFieldValue(field.name, val);
		setStartDate(val);
	};

	const idleLable: boolean = state !== "focused" && !startDate;

	return (
		<OutsideClickSub
			className={`form-group ${
				state === "focused" ? "focused" : state === "error" ? "error" : ""
			} ${disabled ? "disabled" : ""}`}
			callback={() => setFocused(false)}
		>
			<>
				<input
					type="date"
					name={field.name}
					className={`b-none ${
						idleLable ? "trans-none" : "trans-y-7"
					} ff-medium fs-13 w-100 h-100`}
					value={toInputValue(startDate)}
					disabled={disabled}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onChange={handleChange}
				/>
				<label
					className={`form-group-label unclickable ${!idleLable && "small"} ${
						state === "error" && "error"
					}`}
				>
					<span>{label}</span>
				</label>
			</>
		</OutsideClickSub>
	);
};

export default CustomCalendarInput;
